/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */
import { Chip, Link, styled, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridTreeNodeWithRender } from '@mui/x-data-grid';
import { IInstitution } from '@ngt/opms';
import { DateTime } from 'luxon';
import * as React from 'react';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { Patient, PatientStateType, SaConsentStatus } from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));


export const renderSaConsentColumn = (rowData: GridRenderCellParams<Patient, any, any, GridTreeNodeWithRender>, onClick: (patient: Patient) => void, isBct: boolean, hidePending?: boolean) => {
    switch (rowData.row.servicesAustraliaConsentStatus) {
        case SaConsentStatus.Pending:
            return (
                hidePending ? 
                    "" :
                    <Tooltip
                        style={{ width: '100%' }}
                        title={<Typography style={{ fontSize: '0.75rem' }}>Upload SA Consent Document</Typography>}
                        placement="right"
                    >
                        <Link 
                            color="secondary" 
                            onClick={(e) => { e.stopPropagation(); onClick(rowData.row); }}
                        >
                            Pending
                        </Link>
                    </Tooltip>
            )
        case SaConsentStatus.PendingReview:
            return (
                isBct ? 
                    <Tooltip
                        style={{ width: '100%' }}
                        title={<Typography style={{ fontSize: '0.75rem' }}>Review SA Consent Document</Typography>}
                        placement="right"
                    >
                        <Link 
                            color="secondary" 
                            onClick={(e) => { e.stopPropagation(); onClick(rowData.row); }}
                        >
                            Pending Review
                        </Link>
                    </Tooltip> :
                    "Pending Review"
            )
        case SaConsentStatus.Complete:
            return (
                isBct ? 
                    <Tooltip
                        style={{ width: '100%' }}
                        title={<Typography style={{ fontSize: '0.75rem' }}>View Completed SA Consent Document</Typography>}
                        placement="right"
                    >
                        <Link 
                            color="primary" 
                            onClick={(e: any) => { e.stopPropagation(); onClick(rowData.row); }}
                        >
                            Complete
                        </Link>
                    </Tooltip> :
                    "Complete"
            )
        case SaConsentStatus.Processed:
            return (
                isBct ? 
                    <Tooltip
                        style={{ width: '100%' }}
                        title={<Typography style={{ fontSize: '0.75rem' }}>View Processed SA Consent Document</Typography>}
                        placement="right"
                    >
                        <Link 
                            color="primary" 
                            onClick={(e: any) => { e.stopPropagation(); onClick(rowData.row); }}
                        >
                            Processed
                        </Link>
                    </Tooltip> :
                    "Processed"
            )
        default: 
    }
}


/*
 * ---------------------------------------------------------------------------------
 * Hook
 * ---------------------------------------------------------------------------------
 */
const useRegistrationGridColDefs = (
    institutions: IInstitution[] | null, 
    showInitials: boolean, 
    showSite: boolean,
    onSaConsentClick: (patient: Patient) => void,
    isBct: boolean
): GridColDef<Patient>[] => {
    const columns = React.useMemo(() => {
        const c: (GridColDef<Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                headerName: 'Study Number',
                width: 140
                //width: 200
            },
            showInitials && {
                field: 'initials',
                headerName: 'Initials'
            },
            /* showSite && */ {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: rowData => (
                    <StyledChip
                        label={rowData.row.institutionCode}
                        size="small"
                    />
                ),
                width: 110
            },
            /* showSite && */ {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) => institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
            },
            {
                field: 'servicesAustraliaConsentStatus',
                headerName: 'SA Consent',
                renderCell: (rowData) => renderSaConsentColumn(rowData, onSaConsentClick, isBct),
                width: 150
            },
            {
                field: 'patientStateId',
                headerName: 'Status',
                valueFormatter: (_value, row) => row.patientStateId === PatientStateType.Registration ? 'Pre-registered' : 
                        row.patientStateId === PatientStateType.RadiologistReview ? 'Radiologist Review' : 
                            row.patientStateId === PatientStateType.Allocation ? 'Pre-allocated' : '',
                width: 200
            },
            {
                field: 'registrationDate',
                headerName: 'Date Registered',
                renderCell: rowData => (
                    rowData.row.registrationDate ? DateTime.fromISO(rowData.row.registrationDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 150
            }
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [showSite, institutions, showInitials]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useRegistrationGridColDefs;